import React, { memo, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Spinner from "../Spinner";
import { fetchLegalDocument } from "../../api/legal";
import { useLegalUpdate } from "./useLegalUpdate";

// Application surface this build represents — the backend serves surface-specific
// content (patient app vs clinician dashboard).
const LEGAL_SURFACE = "dashboard";

function LegalList({ items }) {
  return (
    <ul className="list-disc pl-6 space-y-1.5 marker:text-primary">
      {items.map((item, index) => {
        if (typeof item === "string") {
          return <li key={index}>{item}</li>;
        }

        return (
          <li key={index}>
            {item.text}
            {Array.isArray(item.list) && item.list.length > 0 ? (
              <LegalList items={item.list} />
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

function LegalBlock({ block }) {
  if (block.heading) {
    return <h2 className="subtitle-text text-typography mt-4">{block.heading}</h2>;
  }

  if (Array.isArray(block.list)) {
    return <LegalList items={block.list} />;
  }

  if (block.text) {
    return <p className="leading-relaxed">{block.text}</p>;
  }

  return null;
}

function LegalDocumentPage({ documentType }) {
  const { t, i18n } = useTranslation(["common", "legal"]);
  const locale = i18n.resolvedLanguage || i18n.language;
  const navigate = useNavigate();
  const {
    outstandingDocuments,
    submitting: acknowledging,
    error: acknowledgeError,
    acknowledgeAll,
  } = useLegalUpdate();

  // The backend Consent Service is the source of truth. We never render a
  // bundled/local copy as a fallback: showing the wrong terms or privacy
  // version is worse than showing a deterministic "unavailable" state.
  const [status, setStatus] = useState("loading"); // loading | loaded | error
  const [document, setDocument] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    setStatus("loading");
    setDocument(null);

    fetchLegalDocument(documentType, { locale, surface: LEGAL_SURFACE })
      .then((resolved) => {
        if (!active) {
          return;
        }

        const body = Array.isArray(resolved?.body) ? resolved.body : [];

        if (resolved && resolved.available !== false && body.length > 0) {
          setDocument(resolved);
          setStatus("loaded");
        } else {
          setStatus("error");
        }
      })
      .catch(() => {
        if (active) {
          setStatus("error");
        }
      });

    return () => {
      active = false;
    };
  }, [documentType, locale, reloadKey]);

  const headingLabel = t(`links.${documentType}`, {
    ns: "legal",
    defaultValue: documentType === "privacy" ? "Privacy Policy" : "Terms & Conditions",
  });

  const containerClass =
    "w-full max-w-5xl min-h-full p-4 md:p-6 flex flex-col gap-4";

  if (status === "loading") {
    return (
      <div className={containerClass}>
        <div
          className="w-full min-h-52 flex flex-col items-center justify-center gap-3"
          role="status"
          aria-live="polite"
        >
          <Spinner />
          <p>
            {t("page.loading", {
              ns: "legal",
              defaultValue: "Loading the current document…",
            })}
          </p>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className={containerClass}>
        <header className="flex flex-col gap-2">
          <h1 className="title-text">{headingLabel}</h1>
        </header>
        <div className="flex flex-col items-start gap-3 text-sm text-greyscale-2">
          <p className="font-semibold text-greyscale-2">
            {t("page.unavailableTitle", {
              ns: "legal",
              defaultValue: "Document unavailable",
            })}
          </p>
          <p>
            {t("page.unavailableBody", {
              ns: "legal",
              defaultValue:
                "We couldn't load the current approved document right now. Please try again in a moment.",
            })}
          </p>
          <button
            type="button"
            className="small-primary-button"
            onClick={() => setReloadKey((key) => key + 1)}
          >
            {t("actions.retry", { ns: "legal", defaultValue: "Try again" })}
          </button>
        </div>
      </div>
    );
  }

  const handleContinue = async () => {
    const acknowledged = await acknowledgeAll();
    if (acknowledged) {
      navigate("/");
    }
  };

  // When the user reached this page from the legal-update flow, offer the same
  // "Agree and continue" action at the top and bottom of the document.
  const continueSection =
    outstandingDocuments.length > 0 ? (
      <div className="flex flex-col gap-1">
        <button
          type="button"
          className="primary-button"
          onClick={handleContinue}
          disabled={acknowledging}
        >
          {acknowledging
            ? t("overlay.saving", { ns: "legal", defaultValue: "Saving…" })
            : t("overlay.agree", {
                ns: "legal",
                defaultValue: "Agree and continue",
              })}
        </button>
        {acknowledgeError ? (
          <p className="text-sm text-greyscale-2">{acknowledgeError}</p>
        ) : null}
      </div>
    ) : null;

  return (
    <div className={containerClass}>
      <header className="flex flex-col gap-2">
        <h1 className="title-text">{document.title}</h1>
        <div className="flex flex-wrap gap-4 text-sm text-greyscale-2">
          <p>
            <span className="font-semibold">
              {t("labelling.version", { defaultValue: "Version" })}:
            </span>{" "}
            {document.version}
          </p>
          <p>
            <span className="font-semibold">{document.effectiveDateLabel}:</span>{" "}
            {document.effectiveDateText}
          </p>
        </div>
      </header>

      {continueSection}

      <article className="flex flex-col gap-3 text-typography leading-relaxed">
        {document.body.map((block, index) => (
          <LegalBlock key={index} block={block} />
        ))}
      </article>

      {continueSection}
    </div>
  );
}

export default memo(LegalDocumentPage);
